import { updatedEntities, denormalisedEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';
import {
  notifyContentSubscriptionListingClosed,
  fetchListingTrashEligibility,
  copyOwnListing,
} from '../../util/api';
import { LISTING_STATE_PUBLISHED } from '../../util/types';
import { isCampaignInboxListing } from '../../util/practitionerCampaign';

import { fetchCurrentUser } from '../../ducks/user.duck';
import { fetchOwnListingTypeCounts } from './listingTypeCounts';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 42 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 42;

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/ManageListingsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/ManageListingsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/ManageListingsPage/FETCH_LISTINGS_ERROR';

export const OPEN_LISTING_REQUEST = 'app/ManageListingsPage/OPEN_LISTING_REQUEST';
export const OPEN_LISTING_SUCCESS = 'app/ManageListingsPage/OPEN_LISTING_SUCCESS';
export const OPEN_LISTING_ERROR = 'app/ManageListingsPage/OPEN_LISTING_ERROR';

export const CLOSE_LISTING_REQUEST = 'app/ManageListingsPage/CLOSE_LISTING_REQUEST';
export const CLOSE_LISTING_SUCCESS = 'app/ManageListingsPage/CLOSE_LISTING_SUCCESS';
export const CLOSE_LISTING_ERROR = 'app/ManageListingsPage/CLOSE_LISTING_ERROR';

export const TRASH_LISTING_REQUEST = 'app/ManageListingsPage/TRASH_LISTING_REQUEST';
export const TRASH_LISTING_SUCCESS = 'app/ManageListingsPage/TRASH_LISTING_SUCCESS';
export const TRASH_LISTING_ERROR = 'app/ManageListingsPage/TRASH_LISTING_ERROR';
export const TRASH_LISTING_CLEAR = 'app/ManageListingsPage/TRASH_LISTING_CLEAR';

export const RESTORE_LISTING_REQUEST = 'app/ManageListingsPage/RESTORE_LISTING_REQUEST';
export const RESTORE_LISTING_SUCCESS = 'app/ManageListingsPage/RESTORE_LISTING_SUCCESS';
export const RESTORE_LISTING_ERROR = 'app/ManageListingsPage/RESTORE_LISTING_ERROR';

export const COPY_LISTING_REQUEST = 'app/ManageListingsPage/COPY_LISTING_REQUEST';
export const COPY_LISTING_SUCCESS = 'app/ManageListingsPage/COPY_LISTING_SUCCESS';
export const COPY_LISTING_ERROR = 'app/ManageListingsPage/COPY_LISTING_ERROR';
export const COPY_LISTING_CLEAR = 'app/ManageListingsPage/COPY_LISTING_CLEAR';

export const ADD_OWN_ENTITIES = 'app/ManageListingsPage/ADD_OWN_ENTITIES';
export const CLEAR_OPEN_LISTING_ERROR = 'app/ManageListingsPage/CLEAR_OPEN_LISTING_ERROR';

export const FETCH_TYPE_COUNTS_REQUEST = 'app/ManageListingsPage/FETCH_TYPE_COUNTS_REQUEST';
export const FETCH_TYPE_COUNTS_SUCCESS = 'app/ManageListingsPage/FETCH_TYPE_COUNTS_SUCCESS';
export const FETCH_TYPE_COUNTS_ERROR = 'app/ManageListingsPage/FETCH_TYPE_COUNTS_ERROR';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  queryParams: null,
  queryInProgress: false,
  queryListingsError: null,
  currentPageResultIds: [],
  ownEntities: {},
  openingListing: null,
  openingListingError: null,
  closingListing: null,
  closingListingError: null,
  trashingListing: null,
  trashingListingError: null,
  restoringListing: null,
  restoringListingError: null,
  copyingListing: null,
  copyingListingError: null,
  listingTypeCounts: null,
  listingTypeCountsInProgress: false,
  listingTypeCountsError: null,
};

const resultIds = data => data.data.map(l => l.id);

const merge = (state, sdkResponse) => {
  const apiResponse = sdkResponse.data;
  return {
    ...state,
    ownEntities: updatedEntities({ ...state.ownEntities }, apiResponse),
  };
};

const updateListingAttributes = (state, listingEntity) => {
  const listingId = listingEntity?.id?.uuid;
  if (!listingId || !listingEntity?.attributes) {
    return state;
  }
  const ownListingMap = state.ownEntities?.ownListing || {};
  const oldListing = ownListingMap[listingId] || {
    id: listingEntity.id,
    type: 'ownListing',
  };
  const updatedListing = { ...oldListing, attributes: listingEntity.attributes };
  const ownListingEntities = {
    ...ownListingMap,
    [listingId]: updatedListing,
  };
  return {
    ...state,
    ownEntities: { ...state.ownEntities, ownListing: ownListingEntities },
  };
};

const manageListingsPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case CLEAR_OPEN_LISTING_ERROR:
      return {
        ...state,
        openingListing: null,
        openingListingError: null,
      };

    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        queryInProgress: true,
        queryListingsError: null,
        currentPageResultIds: [],
      };
    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        queryInProgress: false,
      };
    case FETCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, queryInProgress: false, queryListingsError: payload };

    case OPEN_LISTING_REQUEST:
      return {
        ...state,
        openingListing: payload.listingId,
        openingListingError: null,
      };
    case OPEN_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        openingListing: null,
      };
    case OPEN_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        openingListing: null,
        openingListingError: {
          listingId: state.openingListing,
          error: payload,
        },
      };
    }

    case CLOSE_LISTING_REQUEST:
      return {
        ...state,
        closingListing: payload.listingId,
        closingListingError: null,
      };
    case CLOSE_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        closingListing: null,
      };
    case CLOSE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        closingListing: null,
        closingListingError: {
          listingId: state.closingListing,
          error: payload,
        },
      };
    }

    case TRASH_LISTING_REQUEST:
      return {
        ...state,
        trashingListing: payload.listingId,
        trashingListingError: null,
      };
    case TRASH_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        trashingListing: null,
      };
    case TRASH_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        trashingListing: null,
        trashingListingError: {
          listingId: state.trashingListing,
          error: payload,
        },
      };
    }
    case COPY_LISTING_REQUEST:
      return {
        ...state,
        copyingListing: payload.listingId,
        copyingListingError: null,
      };
    case COPY_LISTING_SUCCESS:
      return {
        ...state,
        copyingListing: null,
      };
    case COPY_LISTING_ERROR:
      return {
        ...state,
        copyingListing: null,
        copyingListingError: {
          listingId: state.copyingListing,
          error: payload,
        },
      };
    case COPY_LISTING_CLEAR:
      return {
        ...state,
        copyingListing: null,
        copyingListingError: null,
      };

    case TRASH_LISTING_CLEAR:
      return {
        ...state,
        trashingListing: null,
        trashingListingError: null,
      };

    case RESTORE_LISTING_REQUEST:
      return {
        ...state,
        restoringListing: payload.listingId,
        restoringListingError: null,
      };
    case RESTORE_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        restoringListing: null,
      };
    case RESTORE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        restoringListing: null,
        restoringListingError: {
          listingId: state.restoringListing,
          error: payload,
        },
      };
    }

    case ADD_OWN_ENTITIES:
      return merge(state, payload);

    case FETCH_TYPE_COUNTS_REQUEST:
      return {
        ...state,
        listingTypeCountsInProgress: true,
        listingTypeCountsError: null,
      };
    case FETCH_TYPE_COUNTS_SUCCESS:
      return {
        ...state,
        listingTypeCounts: payload,
        listingTypeCountsInProgress: false,
        listingTypeCountsError: null,
      };
    case FETCH_TYPE_COUNTS_ERROR:
      return {
        ...state,
        listingTypeCountsInProgress: false,
        listingTypeCountsError: payload,
      };

    default:
      return state;
  }
};

export default manageListingsPageReducer;

// ================ Selectors ================ //

/**
 * Get the denormalised own listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getOwnListingsById = (state, listingIds) => {
  const { ownEntities } = state.ManageListingsPage;
  const resources = listingIds.map(id => ({
    id,
    type: 'ownListing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(ownEntities, resources, throwIfNotFound);
};

// ================ Action creators ================ //

export const clearOpenListingError = () => ({
  type: CLEAR_OPEN_LISTING_ERROR,
});

// This works the same way as addMarketplaceEntities,
// but we don't want to mix own listings with searched listings
// (own listings data contains different info - e.g. exact location etc.)
export const addOwnEntities = sdkResponse => ({
  type: ADD_OWN_ENTITIES,
  payload: sdkResponse,
});

export const openListingRequest = listingId => ({
  type: OPEN_LISTING_REQUEST,
  payload: { listingId },
});

export const openListingSuccess = response => ({
  type: OPEN_LISTING_SUCCESS,
  payload: response.data,
});

export const openListingError = e => ({
  type: OPEN_LISTING_ERROR,
  error: true,
  payload: e,
});

export const closeListingRequest = listingId => ({
  type: CLOSE_LISTING_REQUEST,
  payload: { listingId },
});

export const closeListingSuccess = response => ({
  type: CLOSE_LISTING_SUCCESS,
  payload: response.data,
});

export const closeListingError = e => ({
  type: CLOSE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const trashListingRequest = listingId => ({
  type: TRASH_LISTING_REQUEST,
  payload: { listingId },
});

export const trashListingSuccess = response => ({
  type: TRASH_LISTING_SUCCESS,
  payload: response.data,
});

export const trashListingError = e => ({
  type: TRASH_LISTING_ERROR,
  error: true,
  payload: e,
});

export const trashListingClear = () => ({
  type: TRASH_LISTING_CLEAR,
});

export const restoreListingRequest = listingId => ({
  type: RESTORE_LISTING_REQUEST,
  payload: { listingId },
});

export const restoreListingSuccess = response => ({
  type: RESTORE_LISTING_SUCCESS,
  payload: response.data,
});

export const restoreListingError = e => ({
  type: RESTORE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const copyListingRequest = listingId => ({
  type: COPY_LISTING_REQUEST,
  payload: { listingId },
});

export const copyListingSuccess = result => ({
  type: COPY_LISTING_SUCCESS,
  payload: result,
});

export const copyListingError = e => ({
  type: COPY_LISTING_ERROR,
  error: true,
  payload: e,
});

export const copyListingClear = () => ({
  type: COPY_LISTING_CLEAR,
});

export const queryListingsRequest = queryParams => ({
  type: FETCH_LISTINGS_REQUEST,
  payload: { queryParams },
});

export const queryListingsSuccess = response => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const queryListingsError = e => ({
  type: FETCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const queryTypeCountsRequest = () => ({
  type: FETCH_TYPE_COUNTS_REQUEST,
});

export const queryTypeCountsSuccess = counts => ({
  type: FETCH_TYPE_COUNTS_SUCCESS,
  payload: counts,
});

export const queryTypeCountsError = e => ({
  type: FETCH_TYPE_COUNTS_ERROR,
  error: true,
  payload: e,
});

export const queryOwnListingTypeCounts = () => (dispatch, getState, sdk) => {
  dispatch(queryTypeCountsRequest());
  return fetchOwnListingTypeCounts(sdk)
    .then(counts => {
      dispatch(queryTypeCountsSuccess(counts));
      return counts;
    })
    .catch(e => {
      dispatch(queryTypeCountsError(storableError(e)));
    });
};

// Throwing error for new (loadData may need that info)
export const queryOwnListings = queryParams => (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest(queryParams));

  // Client-only UI params (not Sharetribe query fields)
  const { perPage, status, listingType, ...rest } = queryParams;
  const params = { ...rest, perPage };

  return sdk.ownListings
    .query(params)
    .then(response => {
      const listings = (response?.data?.data || []).filter(
        listing => !isCampaignInboxListing(listing)
      );
      const filtered = {
        ...response,
        data: {
          ...response.data,
          data: listings,
          meta: {
            ...(response.data?.meta || {}),
            totalItems: listings.length,
          },
        },
      };
      dispatch(addOwnEntities(filtered));
      dispatch(queryListingsSuccess(filtered));
      return filtered;
    })
    .catch(e => {
      dispatch(queryListingsError(storableError(e)));
      throw e;
    });
};

export const closeListing = listingId => (dispatch, getState, sdk) => {
  dispatch(closeListingRequest(listingId));

  return sdk.ownListings
    .close({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(closeListingSuccess(response));
      const id = listingId?.uuid || listingId;
      if (id) {
        notifyContentSubscriptionListingClosed({ listingId: id }).catch(() => null);
      }
      return dispatch(queryOwnListingTypeCounts()).then(() => response);
    })
    .catch(e => {
      dispatch(closeListingError(storableError(e)));
    });
};

export const openListing = listingId => (dispatch, getState, sdk) => {
  dispatch(openListingRequest(listingId));

  return sdk.ownListings
    .open({ id: listingId }, { expand: true })
    .then(response => {
      dispatch(openListingSuccess(response));
      return dispatch(queryOwnListingTypeCounts()).then(() => response);
    })
    .catch(e => {
      dispatch(openListingError(storableError(e)));
    });
};

const refreshOwnListingsMaybe = () => (dispatch, getState) => {
  const queryParams = getState().ManageListingsPage?.queryParams;
  const listingsRefresh = queryParams ? dispatch(queryOwnListings(queryParams)) : Promise.resolve();
  return Promise.all([listingsRefresh, dispatch(queryOwnListingTypeCounts())]);
};

const notifyListingClosedMaybe = listingId => {
  const id = listingId?.uuid || listingId;
  if (id) {
    notifyContentSubscriptionListingClosed({ listingId: id }).catch(() => null);
  }
};

export const trashListing = listingId => (dispatch, getState, sdk) => {
  dispatch(trashListingRequest(listingId));

  const idStr = listingId?.uuid || listingId;

  return fetchListingTrashEligibility(idStr)
    .then(eligibility => {
      if (eligibility && eligibility.canTrash === false) {
        const blockers = eligibility.blockers || [];
        const err = new Error('listing_trash_blocked');
        err.code = 'listing_trash_blocked';
        err.blockers = blockers;
        err.activeSubscriptions = eligibility.activeSubscriptions;
        err.futureBookings = eligibility.futureBookings;
        throw err;
      }
      return null;
    })
    .then(() =>
      sdk.ownListings.show({ id: listingId }, { expand: true }).then(showResponse => {
        const listing = showResponse?.data?.data;
        const state = listing?.attributes?.state;
        const closedOnTrash = state === LISTING_STATE_PUBLISHED;

        const closeMaybe = closedOnTrash
          ? sdk.ownListings.close({ id: listingId }, { expand: true }).then(response => {
              notifyListingClosedMaybe(listingId);
              return response;
            })
          : Promise.resolve(null);

        return closeMaybe.then(() =>
          sdk.ownListings.update(
            {
              id: listingId,
              privateData: {
                trash: {
                  trashedAt: new Date().toISOString(),
                  closedOnTrash,
                },
              },
            },
            { expand: true }
          )
        );
      })
    )
    .then(response => {
      dispatch(trashListingSuccess(response));
      // Refresh is best-effort: trash already succeeded on the API.
      return dispatch(refreshOwnListingsMaybe())
        .catch(refreshError => {
          // eslint-disable-next-line no-console
          console.error('trashListing refresh failed', refreshError);
          return response;
        })
        .then(() => response);
    })
    .catch(e => {
      const isBlocked =
        e?.code === 'listing_trash_blocked' || e?.message === 'listing_trash_blocked';
      if (isBlocked) {
        // Expected guard — toast handles messaging; do not show the Oops overlay.
        dispatch(trashListingClear());
      } else {
        dispatch(trashListingError(storableError(e)));
      }
      throw e;
    });
};

export const copyListing = (listingId, { locale } = {}) => dispatch => {
  dispatch(copyListingRequest(listingId));
  const idStr = listingId?.uuid || listingId;

  return copyOwnListing(idStr, { locale })
    .then(result => {
      dispatch(copyListingSuccess(result));
      return dispatch(refreshOwnListingsMaybe())
        .catch(refreshError => {
          // eslint-disable-next-line no-console
          console.error('copyListing refresh failed', refreshError);
          return result;
        })
        .then(() => result);
    })
    .catch(e => {
      const isUnsupported =
        e?.code === 'listing_copy_unsupported' || e?.data?.code === 'listing_copy_unsupported';
      if (isUnsupported) {
        dispatch(copyListingClear());
      } else {
        dispatch(copyListingError(storableError(e)));
      }
      throw e;
    });
};

export const restoreListing = listingId => (dispatch, getState, sdk) => {
  dispatch(restoreListingRequest(listingId));

  return sdk.ownListings
    .show({ id: listingId }, { expand: true })
    .then(showResponse => {
      const listing = showResponse?.data?.data;
      const closedOnTrash = !!listing?.attributes?.privateData?.trash?.closedOnTrash;

      return sdk.ownListings
        .update({ id: listingId, privateData: { trash: null } }, { expand: true })
        .then(updateResponse => {
          if (!closedOnTrash) {
            return updateResponse;
          }
          return sdk.ownListings.open({ id: listingId }, { expand: true });
        });
    })
    .then(response => {
      dispatch(restoreListingSuccess(response));
      return dispatch(refreshOwnListingsMaybe()).then(() => response);
    })
    .catch(e => {
      dispatch(restoreListingError(storableError(e)));
      throw e;
    });
};

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  const queryParams = parse(search);
  const page = queryParams.page || 1;
  dispatch(clearOpenListingError());

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return Promise.all([
    dispatch(fetchCurrentUser()),
    dispatch(
      queryOwnListings({
        ...queryParams,
        page,
        perPage: RESULT_PAGE_SIZE,
        include: ['images', 'currentStock'],
        'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
        ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
        ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
        'limit.images': 1,
      })
    ),
    dispatch(queryOwnListingTypeCounts()),
  ])
    .then(response => {
      // const currentUser = response[0]?.data?.data;
      const ownListings = response[1]?.data?.data;
      return ownListings;
    })
    .catch(e => {
      throw e;
    });
};
